import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import Button from './Button';
import Icon from './Icon';
import { useAuth } from '../../context/AuthContext';
import styles from '../../styles/components/WelcomeModal.module.css';

const NEW_ACCOUNT_THRESHOLD_MS = 24 * 60 * 60 * 1000; // 24 Stunden

/**
 * Welcome Modal - Shown to new users after first login
 * Introduces Monemee and links to the help page
 * Shown only if the account was created within the last 24 hours
 */
function WelcomeModal() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const userId = user?.id;
    const createdAt = user?.createdAt;
    if (!userId || !createdAt) return;

    // Bereits auf diesem Gerät gesehen?
    const hasSeenWelcome = localStorage.getItem(`monemee_welcome_${userId}`);
    if (hasSeenWelcome) return;

    // Nur für Accounts zeigen, die innerhalb der letzten 24 Stunden erstellt wurden,
    // noch keine Produkte haben und Stripe noch nicht verknüpft haben
    const accountAgeMs = Date.now() - new Date(createdAt).getTime();
    const isNewAccount =
      accountAgeMs < NEW_ACCOUNT_THRESHOLD_MS &&
      !user.stripeComplete &&
      (user.productCount ?? 0) === 0;

    if (isNewAccount) {
      const timer = setTimeout(() => setIsOpen(true), 500);
      return () => clearTimeout(timer);
    } else {
      // Bestandsnutzer: localStorage-Flag setzen, damit diese Prüfung nicht
      // bei jedem Seitenaufruf erneut läuft
      localStorage.setItem(`monemee_welcome_${userId}`, 'true');
    }
  }, [user?.id, user?.createdAt, user?.stripeComplete, user?.productCount]);

  const handleClose = () => {
    setIsOpen(false);
    if (user?.id) {
      localStorage.setItem(`monemee_welcome_${user.id}`, 'true');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      showHandle={true}
      closeOnOverlayClick={false}
    >
      <div className={styles.welcomeContent}>
        {/* Header Icon */}
        <div className={styles.headerIcon}>
          <Icon name="sparkles" size={32} />
        </div>

        {/* Title */}
        <h1 className={styles.title}>{t('welcomeModal.title')}</h1>
        <p className={styles.subtitle}>{t('welcomeModal.subtitle')}</p>

        {/* Quick Start Steps */}
        <div className={styles.quickSteps}>
          <div className={styles.step}>
            <div className={styles.stepIcon}>
              <Icon name="creditCard" size="sm" />
            </div>
            <div className={styles.stepText}>
              <strong>{t('welcomeModal.step1Title')}</strong>
              <span>{t('welcomeModal.step1Desc')}</span>
            </div>
          </div>
          <div className={styles.step}>
            <div className={styles.stepIcon}>
              <Icon name="package" size="sm" />
            </div>
            <div className={styles.stepText}>
              <strong>{t('welcomeModal.step2Title')}</strong>
              <span>{t('welcomeModal.step2Desc')}</span>
            </div>
          </div>
          <div className={styles.step}>
            <div className={styles.stepIcon}>
              <Icon name="share2" size="sm" />
            </div>
            <div className={styles.stepText}>
              <strong>{t('welcomeModal.step3Title')}</strong>
              <span>{t('welcomeModal.step3Desc')}</span>
            </div>
          </div>
        </div>

        {/* Help Link */}
        <Link to="/hilfe" className={styles.helpLink} onClick={handleClose}>
          <Icon name="helpCircle" size="sm" />
          <span>{t('welcomeModal.helpLink')}</span>
          <Icon name="chevronRight" size="sm" />
        </Link>

        {/* CTA Button */}
        <Button
          variant="primary"
          fullWidth
          onClick={handleClose}
          size="large"
        >
          {t('welcomeModal.cta')}
        </Button>
      </div>
    </Modal>
  );
}

export default WelcomeModal;
